/**
 * Client-side GitHub Cache
 *
 * Provides frontend caching for GitHub API responses to reduce rate limiting.
 * Uses tiered TTLs:
 * - 5 minutes for static repo metadata (language, stars, description, default branch)
 * - 1 minute for dynamic data (PR info, comment counts)
 *
 * Cached data:
 * - Repository metadata (name, owner, merge methods, language) - 5 min
 * - Last commit info (branch, author) - 5 min
 * - PR basic info (title, author, number) - 1 min
 * - Comment counts - 1 min
 *
 * NOT cached:
 * - Actual comment content
 * - Actual review content
 * - Draft/ready state (changes frequently)
 * - Merge status
 * - CI status
 */

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

export interface CachedRepoMetadata {
  owner: string;
  name: string;
  fullName: string;
  language: string | null;
  defaultBranch: string;
  allowMergeCommit: boolean;
  allowSquashMerge: boolean;
  allowRebaseMerge: boolean;
  stargazersCount: number;
  description?: string;
  pushedAt?: string;
}

export interface CachedLastCommitInfo {
  branch: string;
  authorLogin?: string;
  authorName?: string;
  date?: string;
}

export interface CachedPRMetadata {
  id: number;
  number: number;
  title: string;
  author: {
    login: string;
    avatarUrl: string;
  };
  repository: {
    owner: string;
    name: string;
    fullName: string;
  };
  createdAt: string;
  headRef: string;
  baseRef: string;
}

// 1-minute TTL for dynamic data (PRs, comments)
const CACHE_TTL_DYNAMIC_MS = 60 * 1000;
// 5-minute TTL for static repo metadata
const CACHE_TTL_STATIC_MS = 5 * 60 * 1000;

class GitHubClientCache {
  private repoCache = new Map<string, CacheEntry<CachedRepoMetadata>>();
  private lastCommitCache = new Map<string, CacheEntry<CachedLastCommitInfo>>();
  private prMetaCache = new Map<string, CacheEntry<CachedPRMetadata>>();
  private commentCountCache = new Map<string, CacheEntry<number>>();

  private getRepoKey(owner: string, repo: string): string {
    return `${owner}/${repo}`.toLowerCase();
  }

  private getPRKey(owner: string, repo: string, prNumber: number): string {
    return `${owner}/${repo}/${prNumber}`.toLowerCase();
  }

  private isValid<T>(entry: CacheEntry<T> | undefined): entry is CacheEntry<T> {
    return entry !== undefined && entry.expiresAt > Date.now();
  }

  // Repository metadata (5-minute cache for static data)
  getRepoMetadata(owner: string, repo: string): CachedRepoMetadata | null {
    const key = this.getRepoKey(owner, repo);
    const entry = this.repoCache.get(key);
    return this.isValid(entry) ? entry.data : null;
  }

  setRepoMetadata(owner: string, repo: string, data: CachedRepoMetadata): void {
    const key = this.getRepoKey(owner, repo);
    this.repoCache.set(key, {
      data,
      expiresAt: Date.now() + CACHE_TTL_STATIC_MS, // 5 minutes for static data
    });
  }

  // Last commit info (5-minute cache)
  getLastCommitInfo(owner: string, repo: string): CachedLastCommitInfo | null {
    const key = this.getRepoKey(owner, repo);
    const entry = this.lastCommitCache.get(key);
    return this.isValid(entry) ? entry.data : null;
  }

  setLastCommitInfo(owner: string, repo: string, data: CachedLastCommitInfo): void {
    const key = this.getRepoKey(owner, repo);
    this.lastCommitCache.set(key, {
      data,
      expiresAt: Date.now() + CACHE_TTL_STATIC_MS, // 5 minutes for commit info
    });
  }

  // PR metadata (1-minute cache for dynamic data)
  getPRMetadata(owner: string, repo: string, prNumber: number): CachedPRMetadata | null {
    const key = this.getPRKey(owner, repo, prNumber);
    const entry = this.prMetaCache.get(key);
    return this.isValid(entry) ? entry.data : null;
  }

  setPRMetadata(owner: string, repo: string, prNumber: number, data: CachedPRMetadata): void {
    const key = this.getPRKey(owner, repo, prNumber);
    this.prMetaCache.set(key, {
      data,
      expiresAt: Date.now() + CACHE_TTL_DYNAMIC_MS, // 1 minute for PR data
    });
  }

  // Comment count (1-minute cache for dynamic data)
  getCommentCount(owner: string, repo: string, prNumber: number): number | null {
    const key = this.getPRKey(owner, repo, prNumber);
    const entry = this.commentCountCache.get(key);
    return this.isValid(entry) ? entry.data : null;
  }

  setCommentCount(owner: string, repo: string, prNumber: number, count: number): void {
    const key = this.getPRKey(owner, repo, prNumber);
    this.commentCountCache.set(key, {
      data: count,
      expiresAt: Date.now() + CACHE_TTL_DYNAMIC_MS, // 1 minute for comment counts
    });
  }

  /**
   * Clear all caches (call on manual refresh)
   */
  clearAll(): void {
    this.repoCache.clear();
    this.lastCommitCache.clear();
    this.prMetaCache.clear();
    this.commentCountCache.clear();
  }

  /**
   * Clear only dynamic caches (keeps static repo data)
   */
  clearDynamic(): void {
    this.prMetaCache.clear();
    this.commentCountCache.clear();
  }

  /**
   * Clear cache for a specific PR (after actions like merge, comment, etc.)
   */
  invalidatePR(owner: string, repo: string, prNumber: number): void {
    const key = this.getPRKey(owner, repo, prNumber);
    this.prMetaCache.delete(key);
    this.commentCountCache.delete(key);
  }

  /**
   * Clear cache for a specific repo
   */
  invalidateRepo(owner: string, repo: string): void {
    const key = this.getRepoKey(owner, repo);
    this.repoCache.delete(key);
    this.lastCommitCache.delete(key);
  }

  /**
   * Get cache statistics (useful for debugging)
   */
  getStats(): { repos: number; lastCommits: number; prs: number; commentCounts: number } {
    return {
      repos: this.repoCache.size,
      lastCommits: this.lastCommitCache.size,
      prs: this.prMetaCache.size,
      commentCounts: this.commentCountCache.size,
    };
  }
}

// Singleton instance
export const githubClientCache = new GitHubClientCache();
